import { makeAutoObservable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import UWProductsModule from 'store/modules/uwProducts/uwProductsModule';
import { bpHistory } from 'global/bpHistory';
import { DocumentStatus } from 'types/documentsService';
import ModalDelegate from 'store/general/modalDelegate';
import {
  getRFQProductTemplateChecklistURL,
  ProductTemplatePageType,
} from 'global/checklists-helpers/ProductTemplateChecklistHelper';
import { ProductTemplateChecklistCard } from 'types/productServiceV2';
import { removeBenefitSummary } from 'api/operations/productServiceV2';
import { downloadByUrl } from '@insurancemenu/imenu-components-lib/dist/helpers/downloadByUrl';
import UploadDocumentModalDelegate from './uploadDocumentModalDelegate';

interface DomainData {
  templateId: string;
  type: ProductTemplatePageType;
}

export default class RFQBenefitSummaryPageStore {
  private readonly _domainData: DomainData;
  private _isLoading = false;

  private _uwProductsModule: UWProductsModule = new UWProductsModule();
  private _warningModalDelegate: ModalDelegate = new ModalDelegate();
  public uploadDocumentModalDelegate: UploadDocumentModalDelegate;

  // Getters
  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get showWarningModal(): boolean {
    return this._warningModalDelegate.isVisible;
  }

  public get showUploadDocumentModal(): boolean {
    return this.uploadDocumentModalDelegate.isVisible;
  }

  public get isBenefitSummaryUploaded(): boolean {
    return !!this._uwProductsModule.benefitSummaryLink;
  }

  public get documentStatus(): DocumentStatus {
    return this.isBenefitSummaryUploaded ? DocumentStatus.UPLOADED : DocumentStatus.NOT_UPLOADED;
  }

  public get isTemplateInProgress(): boolean {
    return this._uwProductsModule.isTemplateInProgress;
  }

  // Setters
  private _setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  public openWarningModal(): void {
    this._warningModalDelegate.open();
  }

  public closeWarningModal(): void {
    this._warningModalDelegate.close();
  }

  public openUploadDocumentModal(): void {
    this.uploadDocumentModalDelegate.open();
  }

  public closeUploadDocumentModal(): void {
    this.uploadDocumentModalDelegate.close();
  }

  private async _handleTemplateUpdate(): Promise<void> {
    await runInAction(async () => {
      await this._uwProductsModule.getProductTemplate(this._domainData.templateId);
      this.closeUploadDocumentModal();
    });
  }

  public async getInitialData(): Promise<void> {
    this._setIsLoading(true);
    await this._uwProductsModule.getInitialData(this._domainData.templateId);

    runInAction(() => {
      this._setIsLoading(false);
    });
  }

  public async completeStep(): Promise<void> {
    this._setIsLoading(true);
    await this._uwProductsModule.onCompleteStep(ProductTemplateChecklistCard.BENEFIT_SUMMARY);
    runInAction(() => {
      this._setIsLoading(false);
      this.returnBack();
    });
  }

  public downloadDocument(): void {
    const documentUrl = this._uwProductsModule.benefitSummaryLink;
    if (documentUrl) {
      downloadByUrl(documentUrl);
    }
  }

  public async removeDocument(): Promise<void> {
    this.closeWarningModal();
    this._setIsLoading(true);
    await removeBenefitSummary(this._domainData.templateId);
    await runInAction(async () => {
      await this._uwProductsModule.getProductTemplate(this._domainData.templateId);
      this._setIsLoading(false);
    });
  }

  public returnBack(): void {
    bpHistory.push(
      getRFQProductTemplateChecklistURL({ templateId: this._domainData.templateId, type: this._domainData.type }),
    );
  }

  constructor(domainData: DomainData) {
    this._domainData = domainData;
    this.uploadDocumentModalDelegate = new UploadDocumentModalDelegate({
      templateId: domainData.templateId,
      onUpload: async (): Promise<void> => {
        await this._handleTemplateUpdate();
      },
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const RFQBenefitSummaryPageStoreContext = createContext(new RFQBenefitSummaryPageStore({} as DomainData));

export const useRFQBenefitSummaryPageStore = (): RFQBenefitSummaryPageStore => {
  return useContext(RFQBenefitSummaryPageStoreContext);
};

export { RFQBenefitSummaryPageStoreContext };
